import { PARTICIPANT_EDUCATION_LEVELS, PARTICIPANT_OCCUPATIONS } from '../constants/index';
// ============================================
// Date Utilities
// ============================================
export function formatDate(date, options) {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('id-ID', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        ...options,
    });
}
export function formatDateTime(date) {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleString('id-ID', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}
export function formatRelativeTime(date) {
    const d = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1000);
    if (diffInSeconds < 60)
        return 'Baru saja';
    if (diffInSeconds < 3600)
        return `${Math.floor(diffInSeconds / 60)} menit yang lalu`;
    if (diffInSeconds < 86400)
        return `${Math.floor(diffInSeconds / 3600)} jam yang lalu`;
    if (diffInSeconds < 604800)
        return `${Math.floor(diffInSeconds / 86400)} hari yang lalu`;
    return formatDate(d);
}
/**
 * Parses a date-only value from an HTML date input without allowing JavaScript
 * to silently roll invalid calendar dates into the following month.
 */
export function parseDateOnly(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match)
        return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year &&
        date.getUTCMonth() === month - 1 &&
        date.getUTCDate() === day
        ? date
        : null;
}
/**
 * Calculates completed years using UTC calendar fields. Returns null for an
 * invalid or future date so callers never display a misleading age.
 */
export function calculateAge(dateOfBirth, referenceDate = new Date()) {
    const isDateOnlyValue = typeof dateOfBirth === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth);
    const birthDate = typeof dateOfBirth === 'string'
        ? isDateOnlyValue
            ? parseDateOnly(dateOfBirth)
            : new Date(dateOfBirth)
        : dateOfBirth;
    if (!birthDate || Number.isNaN(birthDate.getTime()))
        return null;
    const birthYear = birthDate.getUTCFullYear();
    const birthMonth = birthDate.getUTCMonth();
    const birthDay = birthDate.getUTCDate();
    const referenceYear = referenceDate.getUTCFullYear();
    const referenceMonth = referenceDate.getUTCMonth();
    const referenceDay = referenceDate.getUTCDate();
    let age = referenceYear - birthYear;
    if (referenceMonth < birthMonth || (referenceMonth === birthMonth && referenceDay < birthDay)) {
        age -= 1;
    }
    return age >= 0 ? age : null;
}
// ============================================
// Participant background utilities
// ============================================
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function readShortText(value) {
    if (typeof value !== 'string')
        return undefined;
    const normalized = value.trim();
    return normalized.length > 0 && normalized.length <= 100 ? normalized : undefined;
}
/** Safely reads optional background metadata from both current and legacy accounts. */
export function getParticipantBackground(value) {
    if (!isRecord(value))
        return {};
    const occupation = PARTICIPANT_OCCUPATIONS.includes(value.occupation)
        ? value.occupation
        : undefined;
    const educationLevel = PARTICIPANT_EDUCATION_LEVELS.includes(value.educationLevel)
        ? value.educationLevel
        : undefined;
    return {
        ...(occupation ? { occupation } : {}),
        ...(occupation === 'OTHER' && readShortText(value.occupationOther)
            ? { occupationOther: readShortText(value.occupationOther) }
            : {}),
        ...(educationLevel ? { educationLevel } : {}),
        ...(educationLevel === 'OTHER' && readShortText(value.educationOther)
            ? { educationOther: readShortText(value.educationOther) }
            : {}),
    };
}
// ============================================
// String Utilities
// ============================================
export function slugify(text) {
    return text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '');
}
export function truncate(text, maxLength, suffix = '...') {
    if (text.length <= maxLength)
        return text;
    return text.slice(0, maxLength - suffix.length) + suffix;
}
export function capitalize(text) {
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}
// ============================================
// Number Utilities
// ============================================
export function formatNumber(num) {
    return num.toLocaleString('id-ID');
}
export function formatPercentage(num, decimals = 0) {
    return `${num.toFixed(decimals)}%`;
}
export function clamp(num, min, max) {
    return Math.min(Math.max(num, min), max);
}
// ============================================
// Array Utilities
// ============================================
export function groupBy(array, key) {
    return array.reduce((groups, item) => {
        const groupKey = String(item[key]);
        return {
            ...groups,
            [groupKey]: [...(groups[groupKey] || []), item],
        };
    }, {});
}
export function uniqueBy(array, key) {
    const seen = new Set();
    return array.filter(item => {
        const val = item[key];
        if (seen.has(val))
            return false;
        seen.add(val);
        return true;
    });
}
// ============================================
// Validation Utilities
// ============================================
export function isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}
/**
 * Normalizes a WhatsApp/telephone number without changing the value shown to
 * the participant. Indonesian domestic forms remain supported, while numbers
 * outside Indonesia must include an explicit international prefix (`+` or
 * `00`) so a numeric participant number (NIP) can never be mistaken for a
 * phone number.
 */
export function normalizePhoneNumber(input) {
    const value = input.trim();
    if (!value || !/^[0-9+().\s-]+$/.test(value))
        return null;
    if ((value.match(/\+/g) ?? []).length > 1 || (value.includes('+') && !value.startsWith('+'))) {
        return null;
    }
    const digits = value.replace(/\D/g, '');
    if (!digits)
        return null;
    let canonical;
    if (digits.startsWith('00')) {
        canonical = digits.slice(2);
    }
    else if (value.startsWith('+')) {
        canonical = digits;
    }
    else if (digits.startsWith('0')) {
        canonical = `62${digits.slice(1)}`;
    }
    else if (digits.startsWith('62')) {
        canonical = digits;
    }
    else {
        // A bare foreign number is ambiguous with the participant number (NIP).
        return null;
    }
    const isIndonesianNumber = canonical.startsWith('62');
    const isValidIndonesianMobile = /^628\d{8,12}$/.test(canonical);
    const isValidInternationalNumber = /^[1-9]\d{6,14}$/.test(canonical);
    if (isIndonesianNumber ? !isValidIndonesianMobile : !isValidInternationalNumber) {
        return null;
    }
    const candidates = new Set([value, digits, canonical, `+${canonical}`, `00${canonical}`]);
    if (isIndonesianNumber) {
        candidates.add(`0${canonical.slice(2)}`);
        candidates.add(canonical.slice(2));
    }
    return { value, canonical, candidates: Array.from(candidates) };
}
export function isValidPhone(phone) {
    return normalizePhoneNumber(phone) !== null;
}
// ============================================
// Type Guards
// ============================================
export function isNonNull(value) {
    return value !== null && value !== undefined;
}
export function hasProperty(obj, prop) {
    return prop in obj;
}
export function getGradePredikat(score) {
    const roundedScore = Math.round(score);
    // 100: A – Mumtaz Murtafi
    if (roundedScore >= 100) {
        return { code: 'MUMTAZ_MURTAFI', arabic: 'مُمْتَاز مُرْتَفِع', indonesian: 'Mumtaz Murtafi' };
    }
    // 90–99: AB – Mumtaz
    if (roundedScore >= 90) {
        return { code: 'MUMTAZ', arabic: 'مُمْتَاز', indonesian: 'Mumtaz' };
    }
    // 80–89: B – Jayyid Jiddan
    if (roundedScore >= 80) {
        return { code: 'JAYYID_JIDDAN', arabic: 'جَيِّد جِدًّا', indonesian: 'Jayyid Jiddan' };
    }
    // 70–79: BC – Jayyid
    if (roundedScore >= 70) {
        return { code: 'JAYYID', arabic: 'جَيِّد', indonesian: 'Jayyid' };
    }
    // 50–69: C – Maqbul
    if (roundedScore >= 50) {
        return { code: 'MAQBUL', arabic: 'مَقْبُول', indonesian: 'Maqbul' };
    }
    // 10–49: D – Rasib
    if (roundedScore >= 10) {
        return { code: 'RASIB', arabic: 'رَاسِب', indonesian: 'Rasib' };
    }
    // 0–9: E – Ghayib
    return { code: 'GHAYIB', arabic: 'غَائِب', indonesian: 'Ghayib' };
}
export function getGradeLetter(score) {
    const rounded = Math.round(score);
    if (rounded >= 100)
        return 'A';
    if (rounded >= 90)
        return 'AB';
    if (rounded >= 80)
        return 'B';
    if (rounded >= 70)
        return 'BC';
    if (rounded >= 50)
        return 'C';
    if (rounded >= 10)
        return 'D';
    return 'E';
}
/**
 * Map a legacy predicate string (from `user_predicates.predicate`) to a
 * bilingual GradePredikat for display. The legacy system stores Indonesian
 * names like "Jayyid", "Maqbul" etc. Return the raw string as fallback
 * when the predicate is unrecognised.
 */
export function getLegacyPredicateDisplay(predicate) {
    const map = {
        // Descriptive grade strings from legacy system
        'Mumtaz Murtafi': {
            code: 'MUMTAZ_MURTAFI',
            arabic: 'مُمْتَاز مُرْتَفِع',
            indonesian: 'Mumtaz Murtafi',
        },
        Mumtaz: {
            code: 'MUMTAZ',
            arabic: 'مُمْتَاز',
            indonesian: 'Mumtaz',
        },
        'Jayyid Jiddan': {
            code: 'JAYYID_JIDDAN',
            arabic: 'جَيِّد جِدًّا',
            indonesian: 'Jayyid Jiddan',
        },
        Jayyid: {
            code: 'JAYYID',
            arabic: 'جَيِّد',
            indonesian: 'Jayyid',
        },
        Maqbul: {
            code: 'MAQBUL',
            arabic: 'مَقْبُول',
            indonesian: 'Maqbul',
        },
        Rasib: {
            code: 'RASIB',
            arabic: 'رَاسِب',
            indonesian: 'Rasib',
        },
        // Letter grades (user-specified scale) from user_predicates.predicate:
        //   100: A – Mumtaz Murtafi, 90–99: AB – Mumtaz, 80–89: B – Jayyid Jiddan,
        //   70–79: BC – Jayyid, 50–69: C – Maqbul, 10–49: D – Rasib, 0–9: E – Ghayib
        A: {
            code: 'MUMTAZ_MURTAFI',
            arabic: 'مُمْتَاز مُرْتَفِع',
            indonesian: 'Mumtaz Murtafi',
        },
        AB: {
            code: 'MUMTAZ',
            arabic: 'مُمْتَاز',
            indonesian: 'Mumtaz',
        },
        B: {
            code: 'JAYYID_JIDDAN',
            arabic: 'جَيِّد جِدًّا',
            indonesian: 'Jayyid Jiddan',
        },
        BC: {
            code: 'JAYYID',
            arabic: 'جَيِّد',
            indonesian: 'Jayyid',
        },
        C: {
            code: 'MAQBUL',
            arabic: 'مَقْبُول',
            indonesian: 'Maqbul',
        },
        D: {
            code: 'RASIB',
            arabic: 'رَاسِب',
            indonesian: 'Rasib',
        },
        E: {
            code: 'GHAYIB',
            arabic: 'غَائِب',
            indonesian: 'Ghayib',
        },
        Ghayib: {
            code: 'GHAYIB',
            arabic: 'غَائِب',
            indonesian: 'Ghayib',
        },
        'N/A': {
            code: 'NA',
            arabic: 'غَيْر مُتَوَفِّر',
            indonesian: 'N/A',
        },
    };
    return map[predicate] ?? { code: 'UNKNOWN', arabic: predicate, indonesian: predicate };
}
/**
 * The letter grades considered authoritative legacy grades (from
 * `user_predicates.predicate`). A numeric `total_grades` backfilled from the
 * IPK weighted average can disagree with the letter, so the letter wins.
 */
const LEGACY_LETTER_GRADES = new Set(['A', 'AB', 'B', 'BC', 'C', 'D', 'E']);
/**
 * Resolve the GradePredikat for an academic transcript record.
 *
 * Priority:
 *  1. Legacy letter predicate (A/AB/B/BC/C/D/E) — authoritative grade.
 *  2. Numeric total_grades via the user's scale.
 *  3. Any other legacy predicate string (descriptive name, N/A, …).
 *  4. null when nothing is available.
 */
export function getTranscriptPredicate(totalGrades, predicate) {
    if (predicate != null && LEGACY_LETTER_GRADES.has(predicate)) {
        return getLegacyPredicateDisplay(predicate);
    }
    if (totalGrades != null) {
        return getGradePredikat(totalGrades);
    }
    if (predicate != null) {
        return getLegacyPredicateDisplay(predicate);
    }
    return null;
}
// ============================================
// Cross-App URL Utilities
// ============================================
/**
 * Resolves the URL for switching between the Participant and Admin apps.
 *
 * Resolution order:
 * 1. Explicit env override: VITE_ADMIN_URL / VITE_PARTICIPANT_URL
 * 2. Localhost / local-IP: swaps ports (admin=3002, participant=3003)
 * 3. Production: maps to pjj.tsl.my.id / my.tsl.my.id
 * 4. Netlify preview deployments: known preview domain mapping
 */
export function getCrossAppUrl(target, envOverrides) {
    if (typeof window === 'undefined') {
        return target === 'admin' ? 'https://pjj.tsl.my.id/login' : 'https://my.tsl.my.id/login';
    }
    const { hostname, protocol } = window.location;
    // 1. Explicit environment variable overrides
    if (target === 'admin' && envOverrides?.adminUrl) {
        return envOverrides.adminUrl;
    }
    if (target === 'participant' && envOverrides?.participantUrl) {
        return envOverrides.participantUrl;
    }
    // 2. Local development: switch ports
    const isLocal = hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        /^192\.168\./.test(hostname) ||
        /^10\./.test(hostname);
    if (isLocal) {
        const targetPort = target === 'admin' ? '3002' : '3003';
        return `${protocol}//${hostname}:${targetPort}/login`;
    }
    // 3. Production domain — swap subdomain
    if (hostname.endsWith('tarbiyahsunnahlearning.or.id') || hostname.endsWith('tsl.my.id')) {
        const subdomain = target === 'admin' ? 'pjj' : 'my';
        return `https://${subdomain}.tsl.my.id/login`;
    }
    // 4. Netlify preview environments
    if (hostname.endsWith('netlify.app')) {
        return target === 'admin'
            ? 'https://web-admin-lms-v3.netlify.app/login'
            : 'https://participant-lms-v3.netlify.app/login';
    }
    // 5. Hardcoded production fallback
    return target === 'admin' ? 'https://pjj.tsl.my.id/login' : 'https://my.tsl.my.id/login';
}
/**
 * Resolves a media / asset URL against a base API origin if it is a relative path.
 */
export function normalizeMediaSource(value) {
    const trimmed = value?.trim();
    if (!trimmed)
        return undefined;
    // Some imported/admin-edited links lost one slash (https:/youtu.be/...)
    // and would otherwise be treated as paths on the API origin.
    return trimmed.replace(/^(https?):\/(?!\/)(?=[^/\s]+\.[^/\s]+(?:\/|$))/i, '$1://');
}
export function resolveMediaUrl(url, baseUrl = 'http://localhost:3001') {
    if (!url || typeof url !== 'string')
        return undefined;
    const trimmed = normalizeMediaSource(url);
    if (!trimmed)
        return undefined;
    const cleanBase = baseUrl.replace(/\/+$/, '');
    let apiOrigin = cleanBase;
    try {
        // API clients construct requests from the origin (`/api/v1/...`).  Keep the
        // same behavior for media URLs even when VITE_API_URL was configured with a
        // path such as `/api/v1`, which otherwise produces duplicated path segments.
        apiOrigin = new URL(cleanBase).origin;
    }
    catch {
        // Preserve support for a relative/custom development base URL.
        apiOrigin = cleanBase.replace(/\/api\/v\d+$/i, '');
    }
    // If Contabo S3 URL, proxy via API storage media endpoint to avoid private S3 401
    try {
        const mediaUrl = new URL(trimmed);
        if (mediaUrl.hostname.toLowerCase().endsWith('.contabostorage.com')) {
            const pathSegments = mediaUrl.pathname.split('/').filter(Boolean);
            const isPathStyleEndpoint = /^(?:[a-z]+\d+|s3\.[a-z]+\d+)\.contabostorage\.com$/i.test(mediaUrl.hostname);
            const keySegments = isPathStyleEndpoint ? pathSegments.slice(1) : pathSegments;
            if (keySegments.length > 0) {
                const encodedKey = keySegments
                    .map(segment => {
                    try {
                        return encodeURIComponent(decodeURIComponent(segment));
                    }
                    catch {
                        return encodeURIComponent(segment);
                    }
                })
                    .join('/');
                return `${apiOrigin}/api/v1/storage/media/${encodedKey}`;
            }
        }
        // Neo Object Storage accepts both path-style and virtual-hosted S3 URLs.
        // Proxy Biznetgio objects through the API so existing private buckets and
        // browser CORS policies never block participant playback.
        const neoHost = mediaUrl.hostname.toLowerCase();
        const neoEndpoint = neoHost.match(/(?:^|\.)(nos\.[a-z0-9-]+\.neo\.id)$/);
        if (neoEndpoint) {
            const pathSegments = mediaUrl.pathname.split('/').filter(Boolean);
            const isVirtualHostedBucket = neoHost !== neoEndpoint[1];
            const bucket = isVirtualHostedBucket
                ? neoHost.slice(0, -(neoEndpoint[1].length + 1))
                : pathSegments[0];
            // Imported arsiptsl videos are public and already support HTTP Range.
            // The configured BIZNETGIO credentials may point to a different bucket;
            // proxying these legacy URLs through it returns 404 instead of video.
            if (bucket === 'arsiptsl')
                return trimmed;
            const keySegments = isVirtualHostedBucket ? pathSegments : pathSegments.slice(1);
            if (keySegments.length > 0) {
                const encodedKey = keySegments
                    .map(segment => {
                    try {
                        return encodeURIComponent(decodeURIComponent(segment));
                    }
                    catch {
                        return encodeURIComponent(segment);
                    }
                })
                    .join('/');
                return `${apiOrigin}/api/v1/storage/media/${encodedKey}?provider=BIZNETGIO`;
            }
        }
    }
    catch {
        // Non-URL values are handled as relative paths below.
    }
    // Legacy local uploads belong to the API media route, not the SPA/static
    // server. The media route also supports video byte ranges.
    let uploadPath = trimmed;
    try {
        const candidate = new URL(trimmed);
        if (candidate.origin === apiOrigin)
            uploadPath = candidate.pathname;
    }
    catch {
        // Relative paths are handled below.
    }
    const localUpload = /^\/?(?:static\/)?uploads\/([^?#]+)(?:[?#].*)?$/i.exec(uploadPath);
    if (localUpload) {
        const rawFilename = localUpload[1].split('/').pop();
        let filename = rawFilename;
        try {
            filename = rawFilename ? decodeURIComponent(rawFilename) : undefined;
        }
        catch {
            // Preserve names containing a literal '%' when the URL is not encoded.
        }
        if (filename) {
            return `${apiOrigin}/api/v1/storage/media/uploads/${encodeURIComponent(filename)}`;
        }
    }
    // If already absolute URL or data/blob URI, return as-is
    if (trimmed.startsWith('http://') ||
        trimmed.startsWith('https://') ||
        trimmed.startsWith('data:') ||
        trimmed.startsWith('blob:')) {
        return trimmed;
    }
    // If relative path like /static/uploads/... or /uploads/...
    const cleanPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    return `${apiOrigin}${cleanPath}`;
}
